import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-ink flex items-center justify-center px-4 spotlight">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md bg-elevate border border-white/10 p-8 sm:p-10 clip-frame"
      >
        <div className="flex items-center gap-3 mb-2">
          <span className="w-3 h-3 bg-blood" />
          <span className="font-display font-extrabold uppercase tracking-widest text-lg">
            Zo <span className="text-blood">Pound</span>
          </span>
        </div>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-gray-500 mb-8">Admin Portal</p>

        <form onSubmit={submit} className="grid gap-4">
          <input
            data-testid="admin-login-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="bg-ink border border-white/15 px-4 py-3 focus:outline-none focus:border-blood text-sm"
          />
          <input
            data-testid="admin-login-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="bg-ink border border-white/15 px-4 py-3 focus:outline-none focus:border-blood text-sm"
          />
          {error && (
            <p data-testid="admin-login-error" className="font-mono text-xs text-blood">{error}</p>
          )}
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={loading}
            className="flex items-center justify-center gap-2 bg-blood hover:bg-red-700 disabled:opacity-50 font-display font-bold uppercase tracking-wider px-6 py-3 transition-colors"
          >
            <Lock size={16} /> {loading ? "Authenticating…" : "Enter the Booth"}
          </button>
        </form>

        <Link
          data-testid="admin-login-back-link"
          to="/"
          className="mt-6 inline-block font-mono text-xs uppercase tracking-widest text-gray-500 hover:text-blood transition-colors"
        >
          ← Back to the show
        </Link>
      </motion.div>
    </div>
  );
}
